import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable, catchError, map, switchMap, throwError} from 'rxjs';
import {
    BusinessComment, BusinessDirectory, BusinessEndorsement, BusinessInquiry, BusinessOpportunity, BusinessPage,
    BusinessSearchParams, DigestResult, ImageUploadTicket
} from '../models/business-directory';
import {API_URL} from '../constants';

@Injectable({
    providedIn: 'root'
})
export class BusinessDirectoryService {
    private baseUrl = `${API_URL}business-directory`;

    constructor(private http: HttpClient) {
    }

    search(params: BusinessSearchParams): Observable<BusinessPage> {
        let p = new HttpParams();
        if (params.q) p = p.set('q', params.q);
        if (params.industries?.length) p = p.set('industries', params.industries.join(','));
        if (params.state) p = p.set('state', params.state);
        if (params.chapterId) p = p.set('chapterId', params.chapterId);
        if (params.lookingFor?.length) p = p.set('lookingFor', params.lookingFor.join(','));
        if (params.mentor) p = p.set('mentor', true);
        if (params.verified) p = p.set('verified', true);
        if (params.ownerId) p = p.set('ownerId', params.ownerId);
        if (params.following) p = p.set('following', true);
        p = p.set('sort', params.sort || 'newest').set('page', params.page ?? 0).set('size', params.size ?? 12);
        return this.http.get<BusinessPage>(`${this.baseUrl}/search`, {params: p});
    }

    getBusinessById(id: string): Observable<BusinessDirectory> {
        return this.http.get<BusinessDirectory>(`${this.baseUrl}/${id}`);
    }

    getMine(): Observable<BusinessDirectory[]> {
        return this.http.get<BusinessDirectory[]>(`${this.baseUrl}/mine`);
    }

    getSimilar(id: string): Observable<BusinessDirectory[]> {
        return this.http.get<BusinessDirectory[]>(`${this.baseUrl}/${id}/similar`);
    }

    getSpotlight(): Observable<BusinessDirectory | null> {
        return this.http.get<BusinessDirectory | null>(`${this.baseUrl}/spotlight`);
    }

    createBusiness(business: BusinessDirectory): Observable<BusinessDirectory> {
        return this.http.post<BusinessDirectory>(this.baseUrl, business);
    }

    updateBusiness(id: string, business: BusinessDirectory): Observable<BusinessDirectory> {
        return this.http.put<BusinessDirectory>(`${this.baseUrl}/${id}`, business);
    }

    deleteBusiness(id: string): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/${id}`);
    }

    hide(id: string, reason?: string): Observable<BusinessDirectory> {
        return this.http.post<BusinessDirectory>(`${this.baseUrl}/${id}/hide`, {reason});
    }

    unhide(id: string): Observable<BusinessDirectory> {
        return this.http.post<BusinessDirectory>(`${this.baseUrl}/${id}/unhide`, {});
    }

    verify(id: string): Observable<BusinessDirectory> {
        return this.http.post<BusinessDirectory>(`${this.baseUrl}/${id}/verify`, {});
    }

    unverify(id: string): Observable<BusinessDirectory> {
        return this.http.delete<BusinessDirectory>(`${this.baseUrl}/${id}/verify`);
    }

    uploadLogo(id: string, file: File): Observable<BusinessDirectory> {
        return this.uploadImage(id, 'LOGO', file);
    }

    uploadPhoto(id: string, file: File): Observable<BusinessDirectory> {
        return this.uploadImage(id, 'PHOTO', file);
    }

    /**
     * Uploads straight to storage: the API signs a short-lived link, the browser PUTs the file there (so it never
     * passes through the API's request-size limit), and the API then verifies it and attaches it to the listing.
     */
    uploadImage(id: string, kind: 'LOGO' | 'PHOTO', file: File): Observable<BusinessDirectory> {
        return this.http.post<ImageUploadTicket>(`${this.baseUrl}/${id}/images/upload-url`,
            {kind, fileName: file.name, contentType: file.type, size: file.size}).pipe(
            switchMap(ticket => this.http.put(ticket.uploadUrl, file, {headers: new HttpHeaders(ticket.headers)}).pipe(
                map(() => ticket),
                // a blocked or expired upload has no useful body; say what happened in words the form can show
                catchError(err => throwError(() => ({
                    ...err,
                    error: {message: err?.status === 403
                            ? 'The upload link expired. Please try again.'
                            : 'The image could not be uploaded to storage. Please try again.'}
                })))
            )),
            switchMap(ticket => this.http.post<BusinessDirectory>(`${this.baseUrl}/${id}/images/confirm`, {kind, fileName: ticket.fileName}))
        );
    }

    removePhoto(id: string, url: string): Observable<BusinessDirectory> {
        return this.http.delete<BusinessDirectory>(`${this.baseUrl}/${id}/photos`, {params: new HttpParams().set('url', url)});
    }

    recordClick(id: string): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/${id}/click`, {});
    }

    // ---- engagement ----

    like(id: string): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/${id}/like`, {});
    }

    unlike(id: string): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/${id}/like`);
    }

    getComments(id: string): Observable<BusinessComment[]> {
        return this.http.get<BusinessComment[]>(`${this.baseUrl}/${id}/comments`);
    }

    addComment(id: string, text: string): Observable<BusinessComment> {
        return this.http.post<BusinessComment>(`${this.baseUrl}/${id}/comments`, {text});
    }

    deleteComment(id: string, commentId: string): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/${id}/comments/${commentId}`);
    }

    getEndorsements(id: string): Observable<BusinessEndorsement[]> {
        return this.http.get<BusinessEndorsement[]>(`${this.baseUrl}/${id}/endorsements`);
    }

    endorse(id: string, note: string): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}/${id}/endorsement`, {note});
    }

    removeEndorsement(id: string): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/${id}/endorsement`);
    }

    follow(id: string): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/${id}/follow`, {});
    }

    unfollow(id: string): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/${id}/follow`);
    }

    sendInquiry(id: string, kind: 'GENERAL' | 'MENTOR', message: string): Observable<BusinessInquiry> {
        return this.http.post<BusinessInquiry>(`${this.baseUrl}/${id}/inquiries`, {kind, message});
    }

    getInquiries(id: string): Observable<BusinessInquiry[]> {
        return this.http.get<BusinessInquiry[]>(`${this.baseUrl}/${id}/inquiries`);
    }

    // ---- opportunities board ----

    getOpportunities(type?: string, q?: string): Observable<BusinessOpportunity[]> {
        let p = new HttpParams();
        if (type) p = p.set('type', type);
        if (q) p = p.set('q', q);
        return this.http.get<BusinessOpportunity[]>(`${this.baseUrl}/opportunities`, {params: p});
    }

    createOpportunity(o: BusinessOpportunity): Observable<BusinessOpportunity> {
        return this.http.post<BusinessOpportunity>(`${this.baseUrl}/opportunities`, o);
    }

    deleteOpportunity(id: string): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/opportunities/${id}`);
    }

    hideOpportunity(id: string, reason?: string): Observable<BusinessOpportunity> {
        return this.http.post<BusinessOpportunity>(`${this.baseUrl}/opportunities/${id}/hide`, {reason});
    }

    unhideOpportunity(id: string): Observable<BusinessOpportunity> {
        return this.http.post<BusinessOpportunity>(`${this.baseUrl}/opportunities/${id}/unhide`, {});
    }

    respondToOpportunity(id: string, message: string): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/opportunities/${id}/respond`, {message});
    }

    // ---- digest ----

    isSubscribed(): Observable<{ subscribed: boolean }> {
        return this.http.get<{ subscribed: boolean }>(`${this.baseUrl}/digest/subscription`);
    }

    subscribe(): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}/digest/subscription`, {});
    }

    unsubscribe(): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/digest/subscription`);
    }

    sendDigest(days: number, dryRun: boolean): Observable<DigestResult> {
        return this.http.post<DigestResult>(`${this.baseUrl}/digest/send`, {}, {params: new HttpParams().set('days', days).set('dryRun', dryRun)});
    }

    private formOf(file: File): FormData {
        const form = new FormData();
        form.append('file', file);
        return form;
    }
}
